import { Chip } from '@mui/material';
import { GridCellParams, GridRenderCellParams, GridSingleSelectColDef } from '@mui/x-data-grid-pro';
import { format } from 'date-fns';
import { PermissionSet, PermissionSetAssignment } from '../../../../types';
import { DetailCTARenderer } from '../../../Compounds/UpstackDataGrid/CellRenderers';
import {
  AccessLevelKey,
  deriveAccessLevel,
  deriveAccessLevelKey,
  deriveDataFilter,
  deriveScopeType
} from '../../../Compounds/PermissionSets/derivations';
import { countUsersForSet } from '../../../Compounds/PermissionSets/permissionSetFilters';
import { PermissionSetActionsCell } from '../../../Compounds/PermissionSets/PermissionSetActionsCell';
import { singleSelectDateOperators } from '../../../Compounds/UpstackDataGrid/relativeDateOperators';

export interface PermissionSetColumnOptions {
  assignments: PermissionSetAssignment[];
  teamMemberCount?: (teamId: number) => number;
  tagNames?: Record<string, string>;
  onDelete: (set: PermissionSet) => void;
}

const SINGLE_SELECT = { type: 'singleSelect' as const, valueOptions: [] };

const formatDate = (iso: string): string => {
  const date = new Date(iso);
  return isNaN(date.getTime()) ? '—' : format(date, 'MMM d, yyyy');
};

const accessLevelColor = (key: AccessLevelKey): 'default' | 'success' | 'warning' =>
  key === 'full_access' ? 'success' : key === 'read_only' ? 'warning' : 'default';

export const buildPermissionSetColumns = ({
  assignments,
  teamMemberCount,
  tagNames,
  onDelete
}: PermissionSetColumnOptions): GridSingleSelectColDef[] => [
  {
    field: 'details',
    headerName: '',
    width: 65,
    sortable: false,
    filterable: false,
    hideable: false,
    disableReorder: true,
    ...SINGLE_SELECT,
    renderCell: (params: GridCellParams) => (
      <DetailCTARenderer
        data={params}
        value={''}
        valueFormatted={''}
      />
    )
  },
  { field: 'name', headerName: 'Name', flex: 1, ...SINGLE_SELECT },
  {
    field: 'scope_type',
    headerName: 'Scope Type',
    width: 150,
    ...SINGLE_SELECT,
    valueGetter: (_value, row: PermissionSet) => deriveScopeType(row.rules)
  },
  {
    field: 'data_filter',
    headerName: 'Data Filter',
    flex: 1,
    ...SINGLE_SELECT,
    valueGetter: (_value, row: PermissionSet) => deriveDataFilter(row.rules, tagNames)
  },
  {
    field: 'users',
    headerName: 'Users',
    width: 90,
    ...SINGLE_SELECT,
    valueGetter: (_value, row: PermissionSet) => countUsersForSet(assignments, row.id, teamMemberCount)
  },
  {
    field: 'access_level',
    headerName: 'Access Level',
    width: 150,
    ...SINGLE_SELECT,
    valueGetter: (_value, row: PermissionSet) => deriveAccessLevel(row.rules),
    renderCell: (params: GridRenderCellParams) => (
      <Chip
        size="small"
        label={params.value}
        color={accessLevelColor(deriveAccessLevelKey((params.row as PermissionSet).rules))}
      />
    )
  },
  {
    field: 'created_at',
    headerName: 'Created',
    width: 140,
    ...SINGLE_SELECT,
    filterOperators: singleSelectDateOperators(),
    valueFormatter: (value: string) => formatDate(value)
  },
  {
    field: 'actions',
    headerName: 'Actions',
    width: 120,
    sortable: false,
    filterable: false,
    hideable: false,
    ...SINGLE_SELECT,
    renderCell: (params: GridRenderCellParams) => (
      <PermissionSetActionsCell
        permissionSet={params.row as PermissionSet}
        onDelete={onDelete}
      />
    )
  }
];
